import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { capitalizeFormattedDate } from 'utils/utils';

/**
 * Custom hook for formatting dates with user preferences from Redux settings
 * @param {Object} overrideOptions - Options to override user settings (e.g., {timeZone: 'UTC'})
 * @returns {Function} Formatter function that takes a date string and returns formatted string
 */
export const useDateFormatter = (overrideOptions = {}) => {
    const { timeZone } = useSelector((state) => state.settings);

    const formatter = useMemo(() => {
        if (!timeZone) {
            // Fallback to defaults if timeZone settings are not available
            // Use browser local timezone (no timeZone property = local time)
            return new Intl.DateTimeFormat('en-AU', {
                weekday: 'short',
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: 'numeric',
                minute: 'numeric',
                second: 'numeric',
                hourCycle: 'h12',
                timeZoneName: 'short',
                ...overrideOptions,
            });
        }

        const { language, ...options } = timeZone;
        return new Intl.DateTimeFormat(language || 'en-AU', {
            ...options,
            ...overrideOptions,
        });
    }, [timeZone, overrideOptions]);

    return (dateString) => {
        if (!dateString) {
            return '';
        }
        if (
            typeof dateString === 'string' &&
            dateString.startsWith('0001-01-01')
        ) {
            return 'Never';
        }

        const normalizedDateString =
            typeof dateString === 'string' &&
            /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?$/.test(dateString)
                ? `${dateString}Z`
                : dateString;

        const date = new Date(normalizedDateString);
        if (isNaN(date.getTime())) {
            return '';
        }
        return capitalizeFormattedDate(formatter.format(date));
    };
};
